
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, PlusCircle, FileText, Edit, Trash2 } from 'lucide-react';
import { 
  fetchModuleLessons, 
  deleteLesson 
} from '@/services/modules/lessonService';
import { ModuleLesson } from '@/types/supabase';
import { useToast } from '@/hooks/use-toast';
import LessonEditor from './LessonEditor';

interface LessonBrowserProps {
  moduleId: string;
  moduleName: string;
}

const LessonBrowser: React.FC<LessonBrowserProps> = ({ moduleId, moduleName }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedLesson, setSelectedLesson] = useState<ModuleLesson | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  // Fetch lessons for the module
  const { data: lessons, isLoading } = useQuery({
    queryKey: ['lessons', moduleId],
    queryFn: () => fetchModuleLessons(moduleId),
  });

  // Delete lesson mutation
  const deleteLessonMutation = useMutation({
    mutationFn: deleteLesson,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons', moduleId] });
      toast({
        title: 'Lesson deleted',
        description: 'The lesson has been deleted successfully',
      });
      setSelectedLesson(null);
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete lesson',
        description: 'There was an error deleting the lesson. Please try again.',
        variant: 'destructive',
      });
      console.error('Error deleting lesson:', error);
    },
  });

  const handleDeleteLesson = (lessonId: string) => {
    if (confirm('Are you sure you want to delete this lesson? This action cannot be undone.')) {
      deleteLessonMutation.mutate(lessonId);
    }
  };

  const handleSuccess = () => {
    setSelectedLesson(null);
    setIsCreatingNew(false);
    queryClient.invalidateQueries({ queryKey: ['lessons', moduleId] });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (selectedLesson || isCreatingNew) {
    return (
      <LessonEditor 
        moduleId={moduleId}
        lessonId={selectedLesson?.id}
        onCancel={() => {
          setSelectedLesson(null);
          setIsCreatingNew(false);
        }}
        onSuccess={handleSuccess}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium">Lessons for {moduleName}</h2>
        <Button 
          onClick={() => setIsCreatingNew(true)}
          size="sm"
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Lesson
        </Button>
      </div>

      {lessons && lessons.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {lessons.map((lesson) => (
            <Card 
              key={lesson.id} 
              className="hover:bg-accent/50 cursor-pointer"
            >
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-start">
                    <h3 className="font-medium">{lesson.title}</h3>
                    <div className="flex flex-col gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLesson(lesson);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0 text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLesson(lesson.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {lesson.description || 'No description available'}
                  </p>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <FileText className="h-3 w-3 mr-1" />
                    <span>{lesson.lesson_type?.replace(/_/g, ' ')}</span>
                    {lesson.duration_minutes && (
                      <span className="ml-2">{lesson.duration_minutes} min</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border rounded-md bg-muted/20">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No lessons yet</h3>
          <p className="text-muted-foreground">
            Get started by creating your first lesson for this module
          </p>
          <Button 
            className="mt-4"
            onClick={() => setIsCreatingNew(true)}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Create First Lesson
          </Button>
        </div>
      )}
    </div>
  );
};

export default LessonBrowser;
